import { useState } from 'react';
import {
  Button,
  ButtonText,
  Checkbox,
  TextArea,
  FormFeedback,
  useToast
} from '@morressier/alembic';
import { Box, Flex, Text } from '@chakra-ui/react';
import { Overlay } from 'src/components/Overlay';
import { Layout } from 'src/components/Layout';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { changeRequestSubmissionSchema } from '@publishing-engine/domain/validation';
import { changeRequestAllowedFields } from 'src/constants';
import { FormFieldMultipleFileUpload } from 'src/components/FormField';
import { useSubmitChangeRequest } from 'src/api/commands';
import { ISubmissionFile } from '@publishing-engine/domain/models';
import { getFileUploadArrayFFDConfigs } from 'src/utils/formHelpers';
import { RequestSubmissionUpdatesConfirmationModal } from './SubmitChangeRequestSubmissionModal';
import { CancelRequestSubmissionUpdatesConfirmationModal } from './CancelChangeRequestSubmitModal';
import { pluralizeString } from 'src/utils/string';
import { createEditorialModal } from '../EditorialDecisionModalWrapper';
import { EditorialNote, useEditorialDecisionNote } from '../EditorialNote';
import { FormFieldTextArea } from '@publishing-engine/domain/types/article';
import { ISubmissionFieldResponseObject } from '@publishing-engine/submission-service/src/usecases/submission-fields/types';

interface Props {
  onClose: () => void;
}

export const SubmitChangeRequest = createEditorialModal(
  ({ onClose }: Props) => {
    const toast = useToast();
    const [showConfirmationModal, setShowConfirmationModal] =
      useState<boolean>(false);
    const [showCancelConfirmationModal, setShowCancelConfirmationModal] =
      useState<boolean>(false);
    const {
      changeRequestData,
      fieldsResponse: { fields },
      submissionId,
      submissionContextId,
      refetchSubmission,
      organizationId
    } = useSubmissionData();

    const licensesAndPermissions = fields.LicensesAndPermissions;
    const licensesAndPermissionsReproducedContent =
      fields.LicensesAndPermissionsReproducedContent
        ? (fields.LicensesAndPermissionsReproducedContent as ISubmissionFieldResponseObject<FormFieldTextArea>)
        : undefined;
    const isReproducedContentRequired =
      licensesAndPermissions?.value ===
      licensesAndPermissionsReproducedContent?.formFieldDefinition.params
        .externalFieldDependency?.value;
      
    const isArticleLanguageEnglish =
      fields.ArticleLanguage?.value === 'English';

    const englishFields = [
      'EnglishTitle',
      'EnglishSubtitle',
      'EnglishAbstract',
      'EnglishKeywords'
    ];

    const allowedCRFieldsByArticle = changeRequestAllowedFields.filter(
      field => {
        if (englishFields.includes(field) && isArticleLanguageEnglish) {
          return false;
        }
        if (
          (field === 'LicensesAndPermissionsReproducedContent' ||
            field === 'LicensesAndPermissionsFiles') &&
          !isReproducedContentRequired
        )
          return false;

        if (field in fields) return true;
        else false;
      }
    );

    const pendingChangeRequestFields =
      changeRequestData?.changeRequest?.fields ?? [];
    const submittedChangeRequestFields = (
      changeRequestData?.changeRequest?.requestHistory ?? []
    ).flatMap(h => h.fields);

    const allChangeRequestFields = Array.from(
      new Set([...pendingChangeRequestFields, ...submittedChangeRequestFields])
    );

    const methods = useForm<{
      fields: { value: string }[];
      reason: string;
      attachments: ISubmissionFile[];
    }>({
      defaultValues: {
        fields: allChangeRequestFields.map(field => ({ value: field })),
        reason: '',
        attachments: []
      },
      resolver: zodResolver(changeRequestSubmissionSchema)
    });

    const submitChangeRequestMutation = useSubmitChangeRequest({
      submissionId,
      organizationId,
      submissionContextId,
      eventSubType: 'form',
      roleContext: 'editorial'
    });

    const { editorNote, setEditorNote, submitEditorNote } =
      useEditorialDecisionNote({
        submissionId,
        submissionContextId,
        organizationId
      });

    const {
      handleSubmit,
      watch,
      register,
      clearErrors,
      getValues,
      setError,
      control,
      formState: { errors }
    } = methods;

    const {
      fields: fieldsFormData,
      append,
      remove
    } = useFieldArray({
      control,
      name: 'fields'
    });

    const filterSubmittedFieldsWithCurrentFields = () => {
      const formValues = getValues();

      const updatedFields = formValues.fields.filter(
        item => !submittedChangeRequestFields.includes(item.value)
      );
      return updatedFields;
    };

    const onSubmit = async () => {
      const formValues = getValues();

      const updatedFields = filterSubmittedFieldsWithCurrentFields();
      const response = await submitChangeRequestMutation.mutateAsync({
        ...formValues,
        fields: updatedFields.map(item => item.value)
      });

      if (response.isSuccess) {
        await submitEditorNote(editorNote);
        toast({
          variant: 'success',
          title: 'Update Request Submitted',
          description:
            'The author has been notified via email to address your update request.'
        });
        refetchSubmission();
        onClose();
      } else {
        toast({
          variant: 'error',
          title: 'Update Request Failed',
          description: 'Please try again later.'
        });
      }
    };

    const submitRequest = () => {
      const updatedFields = filterSubmittedFieldsWithCurrentFields();

      if (!updatedFields.length) {
        setError(
          'fields',
          {
            type: 'min',
            message: 'Fields are required to submit the form'
          },
          {
            shouldFocus: true
          }
        );
        return;
      }

      setShowConfirmationModal(true);
    };

    const currentPendingChangeRequest =
      fieldsFormData.length - submittedChangeRequestFields.length;

    return (
      <Overlay>
        {showConfirmationModal && (
          <RequestSubmissionUpdatesConfirmationModal
            onClose={() => setShowConfirmationModal(false)}
            onSubmit={onSubmit}
          />
        )}
        {showCancelConfirmationModal && (
          <CancelRequestSubmissionUpdatesConfirmationModal
            onExit={onClose}
            onClose={() => setShowCancelConfirmationModal(false)}
          />
        )}
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(submitRequest)}>
            <Layout width="60rem">
              <Flex gap={8} flexDirection="column" justifyContent="start">
                <Flex gap={8} flexDirection="column">
                  <Flex flexDirection="column" gap={1}>
                    <Text textStyle="headingSection">
                      Request Submission Updates
                    </Text>
                    <Text textStyle="textBody">
                      Please review the logged fields and provide additional
                      context for the author, or specify any other fields that
                      need their input.
                    </Text>
                  </Flex>
                  <Flex flexDirection="column" gap={4}>
                    <Flex flexDirection="column" gap={2}>
                      <Text textStyle="headingGroup">Change Requests</Text>
                      <Text textStyle="textBody">
                        Select all relevant fields of this Submission that will
                        require author input.
                      </Text>
                    </Flex>
                    <Flex
                      flexDirection="column"
                      gap={6}
                      padding={8}
                      borderRadius={2}
                      background="surfacePrimaryFill"
                    >
                      <Flex flexDirection="column" gap={1}>
                        <Text textStyle="headingItem">
                          {currentPendingChangeRequest}{' '}
                          {pluralizeString(
                            'Unsent Request',
                            currentPendingChangeRequest
                          )}
                        </Text>
                        <Text textStyle="textBody">
                          You can select more fields requiring author input if
                          needed.
                        </Text>
                      </Flex>
                      <Flex
                        display="grid"
                        gap={4}
                        width="100%"
                        gridTemplateColumns={'repeat(3, minmax(0, 1fr))'}
                        alignItems="center"
                      >
                        {allowedCRFieldsByArticle.map(field => (
                          <Checkbox
                            gap={2}
                            key={field}
                            ariaLabel={field}
                            id={field}
                            name={field}
                            onChange={({ target: { checked } }) => {
                              clearErrors('fields');
                              if (checked) {
                                append({ value: field });
                              } else {
                                const index = fieldsFormData.findIndex(
                                  item => item.value === field
                                );
                                remove(index);
                              }
                            }}
                            isChecked={Boolean(
                              fieldsFormData.some(
                                item => item.value === field
                              ) || submittedChangeRequestFields.includes(field)
                            )}
                            disabled={Boolean(
                              submittedChangeRequestFields.includes(field)
                            )}
                          >
                            <Text textStyle="textBody">
                              {
                                fields[field]?.formFieldDefinition.params.labels
                                  .main
                              }
                            </Text>
                          </Checkbox>
                        ))}
                      </Flex>
                      {errors.fields?.message && (
                        <FormFeedback variant="error">
                          {errors.fields?.message}
                        </FormFeedback>
                      )}
                    </Flex>
                  </Flex>
                  <Flex flexDirection="column" gap={4}>
                    <Flex flexDirection="column" gap={2}>
                      <Text textStyle="headingGroup">
                        Communications to Author
                      </Text>
                      <Text textStyle="textBody">
                        Provide context with your change requests to help the
                        author.
                      </Text>
                    </Flex>
                    <Flex
                      flexDirection="column"
                      gap={6}
                      padding={8}
                      borderRadius={2}
                      background="surfacePrimaryFill"
                    >
                      <Flex gap={2} flexDirection="column">
                        <Flex alignItems="flex-start" gap={2}>
                          <Text textStyle="headingItem">
                            Response to Author
                          </Text>
                          <Box
                            as="span"
                            color="actionSecondaryCautionContent"
                            textStyle="headingItem"
                          >
                            *
                          </Box>
                        </Flex>
                        <TextArea
                          value={watch('reason')}
                          {...register('reason')}
                          placeholder="Add feedback to the author"
                          isInvalid={Boolean(errors.reason)}
                          errorMessage={errors?.reason?.message}
                          rows={2}
                        />
                      </Flex>
                      <Flex gap={2} flexDirection="column">
                        <FormFieldMultipleFileUpload
                          config={getFileUploadArrayFFDConfigs('attachments')}
                          submissionId={submissionId}
                          submissionContextId={submissionContextId}
                        />
                      </Flex>
                    </Flex>
                  </Flex>
                  <EditorialNote value={editorNote} onChange={setEditorNote} />
                </Flex>
              </Flex>
            </Layout>
            <Flex
              paddingY={4}
              bottom={0}
              zIndex={1}
              justifyContent="center"
              position="sticky"
              bgColor="surfacePrimaryFill"
              marginX="auto"
            >
              <Flex
                width="100%"
                maxWidth="960px"
                justifyContent="flex-end"
                gap={4}
              >
                {/* ----- Footer Buttons ----- */}
                <ButtonText
                  variant="caution"
                  onClick={() => setShowCancelConfirmationModal(true)}
                >
                  Cancel
                </ButtonText>
                <Button variant="primary" type="submit">
                  Request Updates
                </Button>
              </Flex>
            </Flex>
          </form>
        </FormProvider>
      </Overlay>
    );
  }
);
